import { useEffect, useState } from 'react';
import { ReactComponent as UploadFileIcon } from '@/assets/img/upload-pet-image.svg';
import { ReactComponent as PdfIcon } from '@/assets/img/pdf-icon.svg';
import { ReactComponent as DeleteIcon } from '@/assets/img/delete-icon-v2.svg';
import './VaccinationFieldComponent.scss';
import { Loader } from '@/components/Loader/Loader';
import { Vaccine, VaccineFormData } from '../types';
import Utils from '@/utils/util';
import { Day, DayValue } from 'react-modern-calendar-datepicker';
import moment from 'moment';
import { UploadFileResponse, useUploadFile } from '../api/getFields';
import { showNotification } from '@/utils/notificationUtils';
import { DropDown } from '@/components/dropdown/DropDown';
import DatePickerWithPortal from '@/components/DatePicker/DatepickerWithPortal';
import { t } from 'i18next';
import WebcamModal from '@/components/WebCam/WebCam';
import { FileUploader } from '@/features/customers/components/FileUploader';
import HybridFileUploader from '@/components/FileUploader/FileUploader';
type VaccinationFieldComponentProps = {
  data?: VaccineFormData;
  onChange?: (data: VaccineFormData) => void;
  disabled?: boolean;
};
export const VaccinationFieldComponent = ({
  data,
  disabled,
  onChange,
}: VaccinationFieldComponentProps) => {
  const TYPE = {
    TAKEN_DATE: 'takenDate',
    EXPIRY_DATE: 'expiryDate',
  };
  const [day] = useState<Day>({
    day: moment().date(),
    month: moment().month() + 1,
    year: moment().year(),
  } as Day);
  const uploadPictureMutation = useUploadFile();
  const [vaccineData, setVaccineData] = useState<Vaccine>();
  const [vaccineDataList, setVaccineDataList] = useState<Vaccine[]>([]);
  const [fileResponse, setFileResponse] = useState<UploadFileResponse>();

  useEffect(() => {
    if (data?.vaccineList && data?.vaccineList.length) {
      setVaccineDataList(data.vaccineList);
    } else {
      setVaccineDataList([]);
    }
    if (!data?.vaccine) {
      setVaccineData(undefined);
    } else {
      setVaccineData(data.vaccine);
    }
  }, [data]);

  const validateFile = (file: File) => {
    if (file.size > 10000000) {
      showNotification({
        type: 'info',
        title: 'Uploaded file should be less than 10 mb .',
      });
      return false;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

    if (!validTypes.includes(file.type)) {
      showNotification({
        type: 'info',
        title: 'Only png, jpg images or pdf file is allowed',
      });
      return false;
    }

    return true;
  };

  const onFileSelect = (file: File) => {
    uploadPetDocuments(file);
  };

  const uploadPetDocuments = async (selectedFiles: any) => {
    if (selectedFiles) {
      const formData = new FormData();
      formData.append('file', selectedFiles);
      const response = await uploadPictureMutation.mutateAsync({
        file: formData,
      });
      setFileResponse(response);
      const data = { ...vaccineData };
      data.documentLink = response.fileUrl;
      data.fileName = response.fileName;
      data.fileSize = response.fileSize;
      setVaccineData(data);
      updateVaccineData(vaccineDataList, data);
    }
  };
  const onVaccinationTypeChange = (type: string) => {
    const data = { ...vaccineData };
    data.type = type;
    setVaccineData(data);
    updateVaccineData(vaccineDataList, data);
  };
  const onDateChange = (value: string, type = TYPE.TAKEN_DATE) => {
    const data = { ...vaccineData };
    if (type === TYPE.TAKEN_DATE) {
      data.takenDate = value;
    } else {
      data.expiryDate = value;
    }
    setVaccineData(data);
    updateVaccineData(vaccineDataList, data);
  };
  const updateVaccineData = (vaccineData: Vaccine[] | any, data?: Vaccine) => {
    onChange && onChange({ vaccineList: [...vaccineData], vaccine: { ...data } });
  };
  const validateVaccinationData = () => {
    let errorMessage = '';
    if (vaccineData) {
      const { takenDate, expiryDate, type, documentLink } = vaccineData;
      if (!type || type.trim().length < 1) {
        errorMessage = 'Please select vaccination type';
      } else if (!takenDate || takenDate.trim().length < 1) {
        errorMessage = 'Please select vaccination taken date';
      } else if (!expiryDate || expiryDate.trim().length < 1) {
        errorMessage = 'Please select vaccination expiry date';
      } else if (!documentLink || documentLink.trim().length < 1) {
        errorMessage = 'Please upload vaccination document';
      }
    } else {
      errorMessage = 'Please select vaccination type';
    }
    return errorMessage;
  };
  const addNewVaccination = () => {
    if (uploadPictureMutation.isLoading) {
      return;
    }
    const errorMessage = validateVaccinationData();
    if (errorMessage) {
      showNotification({
        type: 'error',
        title: errorMessage,
      });
      return;
    }
    if (vaccineData) {
      const isAvailableAlready = vaccineDataList.find((vd) => vd.type === vaccineData.type);
      if (!isAvailableAlready) {
        vaccineDataList?.push(vaccineData);
        setVaccineData(undefined);
        //setVaccineDataList([...vaccineDataList]);
        updateVaccineData([...vaccineDataList], undefined);
      } else {
        showNotification({
          type: 'error',
          title: `Vaccination details for ${
            Utils.VACCINE_TYPES.find((vt) => vt.value === vaccineData.type)?.label
          } is added already.`,
        });
      }
    }
  };
  const removeVaccineData = (index: number) => {
    vaccineDataList.splice(index, 1);
    // setVaccineDataList([...vaccineDataList]);
    updateVaccineData([...vaccineDataList], undefined);
  };
  let validateData = true;
  if (vaccineData) {
    const { takenDate, expiryDate, type, documentLink } = vaccineData;
    if (!expiryDate && !takenDate && !type && !documentLink) {
      validateData = false;
    }
  } else {
    validateData = false;
  }

  const handleCapture = (file: File) => {
    uploadPetDocuments(file);
  };
  const getCard = (vd: Vaccine, index: number) => {
    return (
      <div className="insurance-card">
        <div className="left-details">
          {vd.documentLink && <PdfIcon />}
          <div className="file-details">
            <p>{Utils.VACCINE_TYPES?.find((vt: any) => vt.value == vd?.type)?.label}</p>
            <p>
              {vd.takenDate && (
                <span className="mr-3">
                  Taken on {Utils.formatDateStringTz(vd.takenDate, undefined, 'MMM, DD yyyy')}
                </span>
              )}
              {vd.expiryDate && (
                <span>
                  Expiry on {Utils.formatDateStringTz(vd.expiryDate, undefined, 'MMM, DD yyyy')}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="right-action">
          <DeleteIcon
            onClick={(e: any) => {
              e?.stopPropagation();
              e?.preventDefault();
              removeVaccineData(index);
            }}
          />
        </div>
      </div>
    );
  };
  return (
    <div className="pet-vaccinations">
      {vaccineDataList.length > 0 && (
        <div className="vaccine-list">
          {vaccineDataList.map(
            (vd: Vaccine, index: number) =>
              vd?.type &&
              (vd.documentLink ? (
                <a href={vd.documentLink} target="_blank" rel="noreferrer">
                  {getCard(vd, index)}
                </a>
              ) : (
                getCard(vd, index)
              ))
          )}
        </div>
      )}
      <div className="pet-vaccine-fields">
        <div className="pet-row">
          <div className="pet-col-12">
            <DropDown
              placeholder="Select vaccination type"
              selectedValue={vaccineData?.type}
              data={Utils.VACCINE_TYPES}
              onSelect={(value: any) => {
                onVaccinationTypeChange(value);
              }}
              onDismiss={() => {}}
              enableSearch={false}
              isDisabled={disabled}
              customClass={vaccineData?.type ? '' : validateData ? 'empty' : ''}
            />
          </div>
        </div>
        <div className="pet-row">
          <div className="pet-col-6">
            <DatePickerWithPortal
              value={vaccineData?.takenDate}
              onChange={(val: DayValue) => {
                onDateChange(`${val?.month}/${val?.day}/${val?.year}`, TYPE.TAKEN_DATE);
              }}
              disabled={disabled}
              placeholder={t('Date taken')}
              maximumDate={day}
              validateData={validateData}
            />
          </div>
          <div className="pet-col-6">
            <DatePickerWithPortal
              value={vaccineData?.expiryDate}
              onChange={(val: DayValue) => {
                onDateChange(`${val?.month}/${val?.day}/${val?.year}`, TYPE.EXPIRY_DATE);
              }}
              placeholder={t('Expiry date')}
              disabled={disabled}
              validateData={validateData}
            />
          </div>
        </div>

        {vaccineData?.documentLink ? (
          <a href={vaccineData.documentLink} target="_blank" rel="noreferrer">
            <div className="insurance-card">
              <div className="left-details">
                <PdfIcon />
                <div className="file-details">
                  <p>{'Vaccination Document'}</p>
                </div>
              </div>
              <div className="right-action">
                <DeleteIcon
                  onClick={(e: any) => {
                    e?.stopPropagation();
                    e?.preventDefault();
                    setFileResponse(undefined);
                    vaccineData.documentLink = '';
                    setVaccineData({ ...vaccineData });
                  }}
                />
              </div>
            </div>
          </a>
        ) : (
          <>
            <HybridFileUploader
              isLoading={uploadPictureMutation.isLoading}
              onFileSelect={onFileSelect}
              fuLabel="file-uploader"
              onlyFile={true}
              showLoader={true}
              validateFile={validateFile}
              disabled={disabled}
            />
            {Utils.isMobile() ? (
              <></>
            ) : (
              !uploadPictureMutation.isLoading && (
                <WebcamModal disabled={disabled} onCapture={handleCapture} />
              )
            )}
          </>
        )}
        {!disabled && (
          <div
            className={`action-vaccine ${uploadPictureMutation.isLoading ? 'disabled' : ''}`}
            onClick={addNewVaccination}
          >
            <span>+ Add new vaccination</span>
          </div>
        )}
      </div>
    </div>
  );
};
